import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { personLabel } from '../../components/ApprovalLevels.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface AuditRow {
  readonly auditLogId: string;
  readonly eventType: string;
  readonly entityType: string | null;
  readonly entityCode: string | null;
  readonly action: string;
  readonly actorName: string | null;
  readonly actorCode: string | null;
  readonly eventTimestamp: string;
}

const AUDIT_ACTIONS = ['CREATE', 'UPDATE', 'DELETE', 'ACTIVATE', 'SUSPEND', 'CANCEL', 'RENEW', 'UPGRADE', 'DOWNGRADE', 'BILL', 'INVOICE', 'PAYMENT', 'REVERSE', 'DISCOUNT', 'TAX', 'ANALYTICS', 'SECURITY'];
const PAGE = 25;

/**
 * M44 §26 Commercial Audit Dashboard (§28). Auditors browse the append-only
 * commercial audit ledger (BR-02). This is read-only — it requires
 * M44_AUDIT_VIEW; the trail is immutable (no create / edit / delete).
 */
export function CommercialAuditDashboard(): ReactNode {
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [total, setTotal] = useState(0);
  const [actionFilter, setActionFilter] = useState('');
  const [eventFilter, setEventFilter] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (actionFilter) params.set('action', actionFilter);
    if (eventFilter.trim()) params.set('eventType', eventFilter.trim());
    params.set('limit', String(PAGE));
    params.set('offset', String(page * PAGE));
    apiRequest<{ data: AuditRow[]; total: number }>(`/v1/billing/audit?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, actionFilter, eventFilter, page]);

  return (
    <main aria-label="commercial-audit-dashboard">
      <h1>Commercial Audit Dashboard</h1>

      <section aria-label="audit-filters">
        <label>Action
          <select aria-label="filter-action" value={actionFilter} onChange={(e) => { setActionFilter(e.target.value); setPage(0); }}>
            <option value="">All</option>
            {AUDIT_ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </label>
        <label>Event Type<input aria-label="filter-event" value={eventFilter} onChange={(e) => { setEventFilter(e.target.value); setPage(0); }} /></label>
      </section>

      {error && <p role="alert" aria-label="audit-error">{error}</p>}
      {loading ? <p>Loading…</p> : (
        <>
          <p aria-label="audit-total">{total} audit entr(y/ies).</p>
          <table aria-label="audit-table">
            <thead>
              <tr><th scope="col">Event</th><th scope="col">Entity</th><th scope="col">Code</th><th scope="col">Action</th><th scope="col">Actor</th><th scope="col">Timestamp</th></tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={6}>No audit entries.</td></tr>
              ) : rows.map((a) => (
                <tr key={a.auditLogId} data-audit={a.auditLogId}>
                  <td aria-label={`audit-event-${a.auditLogId}`}>{a.eventType}</td>
                  <td>{a.entityType ?? '—'}</td>
                  <td>{a.entityCode ?? '—'}</td>
                  <td aria-label={`audit-action-${a.auditLogId}`}>{a.action}</td>
                  <td>{personLabel(a.actorName, a.actorCode) ?? '—'}</td>
                  <td>{at(a.eventTimestamp)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <nav aria-label="audit-pagination">
            <button type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</button>
            <span aria-label="page-indicator">Page {page + 1} of {Math.max(1, Math.ceil(total / PAGE))}</span>
            <button type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>Next</button>
          </nav>
        </>
      )}
    </main>
  );
}
